(() => {
  const ROOT_SELECTOR = '.khushi-carousel-section[data-khushi-carousel]';
  const SLIDESHOW_SELECTOR = 'slideshow-component[data-khushi-native-carousel]';
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const READY = new WeakSet();
  const STATE = new WeakMap();

  function getState(root) {
    let state = STATE.get(root);
    if (!state) {
      state = { settleTimer: 0, editorPaused: false, cleanups: [] };
      STATE.set(root, state);
    }
    return state;
  }

  function setupProgressA11y(slideshow) {
    const list = slideshow.querySelector('.khushi-carousel-progress .slideshow-controls__dots');
    if (!(list instanceof HTMLElement)) return;

    // Horizon owns selection state via aria-selected. Give those native dot
    // buttons the matching tab semantics without modifying Horizon's snippet.
    list.setAttribute('role', 'tablist');
    list.querySelectorAll(':scope > li').forEach((item) => item.setAttribute('role', 'presentation'));
    list.querySelectorAll('[ref="dots[]"]').forEach((dot) => dot.setAttribute('role', 'tab'));
  }

  function stopProgress(slideshow) {
    slideshow
      .querySelectorAll('.khushi-carousel-progress [ref="dots[]"]')
      .forEach((dot) => dot.classList.remove('khushi-carousel-progress-running'));
  }

  function restartProgress(slideshow) {
    stopProgress(slideshow);
    const current = slideshow.querySelector(
      '.khushi-carousel-progress [ref="dots[]"][aria-selected="true"]'
    );
    if (!(current instanceof HTMLElement)) return;

    // Alternate between two identical animation names so the 0 -> 100% fill
    // restarts without a synchronous layout read / forced reflow.
    current.dataset.khushiProgressCycle =
      current.dataset.khushiProgressCycle === 'a' ? 'b' : 'a';
    current.classList.add('khushi-carousel-progress-running');
  }

  function shouldAutoplay(root, slideshow) {
    const state = getState(root);
    return Boolean(
      MOBILE.matches &&
      root.dataset.mobileAutoplay === 'true' &&
      slideshow.hasAttribute('autoplay') &&
      !REDUCED.matches &&
      !document.hidden &&
      !state.editorPaused
    );
  }

  function syncMode(root) {
    const slideshow = root.querySelector(SLIDESHOW_SELECTOR);
    if (!(slideshow instanceof HTMLElement)) return;

    const desktopGrid = !MOBILE.matches && root.dataset.desktopLayout === 'grid';
    slideshow.disabled = desktopGrid;

    if (!shouldAutoplay(root, slideshow)) {
      slideshow.suspend?.();
      stopProgress(slideshow);
      return;
    }

    // Horizon owns the actual autoplay interval and infinite slide selection.
    slideshow.suspend?.();
    slideshow.play?.();
    restartProgress(slideshow);
  }

  function scheduleNativeRestart(root, delay = 180) {
    const state = getState(root);
    if (state.settleTimer) window.clearTimeout(state.settleTimer);
    state.settleTimer = window.setTimeout(() => {
      state.settleTimer = 0;
      syncMode(root);
    }, delay);
  }

  function listen(state, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    state.cleanups.push(() => target.removeEventListener(type, handler, options));
  }

  function listenMedia(state, query, handler) {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handler);
      state.cleanups.push(() => query.removeEventListener('change', handler));
    } else if (typeof query.addListener === 'function') {
      query.addListener(handler);
      state.cleanups.push(() => query.removeListener(handler));
    }
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || READY.has(root)) return;
    const slideshow = root.querySelector(SLIDESHOW_SELECTOR);
    if (!(slideshow instanceof HTMLElement)) return;

    READY.add(root);
    const state = getState(root);
    setupProgressA11y(slideshow);

    const onNativeSelect = (event) => {
      // The active slide/dot comes only from Horizon. We merely restart the
      // native interval after a customer-driven swipe/click so its 3-second
      // clock and the Khushi fill animation begin together.
      if (event.detail?.userInitiated) {
        slideshow.suspend?.();
        stopProgress(slideshow);
        scheduleNativeRestart(root, event.detail?.trigger === 'scroll' ? 220 : 80);
        return;
      }

      if (shouldAutoplay(root, slideshow)) restartProgress(slideshow);
    };

    const onPointerDown = () => {
      if (!MOBILE.matches) return;
      slideshow.suspend?.();
      stopProgress(slideshow);
      root.setAttribute('data-khushi-interacting', 'true');
    };

    const onPointerEnd = () => {
      if (!MOBILE.matches) return;
      root.removeAttribute('data-khushi-interacting');
      scheduleNativeRestart(root, 220);
    };

    const onBlockSelect = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const slide = target?.closest('slideshow-slide');
      if (!(slide instanceof HTMLElement)) return;

      const slides = Array.from(slideshow.querySelectorAll(':scope > slideshow-container > slideshow-slides > slideshow-slide'));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      state.editorPaused = true;
      slideshow.suspend?.();
      stopProgress(slideshow);
      slideshow.select?.(index, undefined, { animate: false });
    };

    const onBlockDeselect = () => {
      state.editorPaused = false;
      scheduleNativeRestart(root, 0);
    };

    const onPreferenceChange = () => syncMode(root);
    const onVisibilityChange = () => syncMode(root);

    listen(state, slideshow, 'slideshow:select', onNativeSelect);
    listen(state, slideshow, 'pointerdown', onPointerDown, { passive: true });
    listen(state, slideshow, 'pointerup', onPointerEnd, { passive: true });
    listen(state, slideshow, 'pointercancel', onPointerEnd, { passive: true });
    listen(state, root, 'shopify:block:select', onBlockSelect);
    listen(state, root, 'shopify:block:deselect', onBlockDeselect);
    listen(state, document, 'visibilitychange', onVisibilityChange);
    listenMedia(state, MOBILE, onPreferenceChange);
    listenMedia(state, REDUCED, onPreferenceChange);

    requestAnimationFrame(() => syncMode(root));
  }

  function initAll(root = document) {
    if (root instanceof Element && root.matches(ROOT_SELECTOR)) init(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  function boot() {
    customElements.whenDefined('slideshow-component').then(() => initAll());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    customElements.whenDefined('slideshow-component').then(() => initAll(event.target));
  });
})();
